import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Plus, RefreshCw } from 'lucide-react'

import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { TemplateStatusBadge } from '@/components/TemplateStatusBadge'
import { useAuthStore } from '@/stores/auth'
import type {
  Template,
  TemplateCategory,
  TemplateListResponse,
  TemplateStatus,
  TemplateSyncResponse,
} from '@sdr-agend/admin-schemas'

const STATUSES: TemplateStatus[] = [
  'DRAFT',
  'PENDING_META',
  'APPROVED',
  'REJECTED',
  'DISABLED',
]

const CATEGORIES: TemplateCategory[] = ['UTILITY', 'MARKETING', 'AUTHENTICATION']

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

/**
 * `/admin/templates` — registry list with status/category/search filters and
 * a Meta sync button.
 *
 * RBAC:
 *   - All authenticated users can read.
 *   - "Sync com Meta" and "Novo Template" are gated to admin/manager.
 */
export function TemplatesPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canMutate = role === 'admin' || role === 'manager'
  const queryClient = useQueryClient()

  const [statusFilter, setStatusFilter] = useState<Set<TemplateStatus>>(new Set())
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>('')
  const [search, setSearch] = useState('')
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })

  const queryKey = useMemo(
    () => ['admin-templates', { statusFilter: [...statusFilter], categoryFilter, search }],
    [statusFilter, categoryFilter, search],
  )

  const { data, isLoading, isError } = useQuery<TemplateListResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter.size > 0) params.set('status', [...statusFilter].join(','))
      if (categoryFilter !== '') params.set('category', categoryFilter)
      if (search.trim() !== '') params.set('search', search.trim())
      const qs = params.toString()
      return api<TemplateListResponse>(`/admin/templates${qs === '' ? '' : `?${qs}`}`)
    },
  })

  const syncMutation = useMutation({
    mutationFn: async () =>
      api<TemplateSyncResponse>('/admin/templates/sync', { method: 'POST' }),
    onSuccess: (res) => {
      const parts: string[] = []
      if (res.imported > 0) parts.push(`${res.imported} importados da Meta`)
      if (res.updated > 0) parts.push(`${res.updated} status atualizados`)
      if (res.categoryChanged > 0) parts.push(`${res.categoryChanged} categorias atualizadas`)
      const toMarketing = res.categoryChanges.filter((c) => c.to === 'MARKETING')
      if (toMarketing.length > 0) {
        parts.push(
          `atenção: ${toMarketing.map((c) => c.name).join(', ')} agora é Marketing (custo maior por mensagem)`,
        )
      }
      if (res.synced > 0 && res.imported === 0 && res.updated === 0 && res.categoryChanged === 0) {
        parts.push(`${res.synced} já sincronizados`)
      }
      if (res.skipped > 0) parts.push(`${res.skipped} ignorados (idioma/categoria/body inválidos)`)
      setToast({
        open: true,
        variant: 'default',
        title: 'Sincronização concluída',
        description: parts.length === 0 ? 'Nada para sincronizar.' : parts.join(' · '),
      })
      void queryClient.invalidateQueries({ queryKey: ['admin-templates'] })
    },
    onError: (err: Error) => {
      setToast({
        open: true,
        variant: 'destructive',
        title: 'Falha ao sincronizar',
        description: err.message,
      })
    },
  })

  const toggleStatus = (s: TemplateStatus): void => {
    setStatusFilter((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Templates</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie templates HSM enviados ao WhatsApp via Meta.
          </p>
        </div>
        <div className="flex gap-2">
          {canMutate && (
            <Button
              type="button"
              variant="outline"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {syncMutation.isPending ? 'Sincronizando…' : 'Sync com Meta'}
            </Button>
          )}
          {canMutate && (
            <Button asChild>
              <Link to="/admin/templates/new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Novo Template
              </Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar por status">
          {STATUSES.map((s) => {
            const active = statusFilter.has(s)
            return (
              <Button
                key={s}
                type="button"
                variant={active ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleStatus(s)}
                aria-pressed={active}
              >
                {s}
              </Button>
            )
          })}
        </div>
        <Select
          aria-label="Filtrar por categoria"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | '')}
          className="max-w-[12rem]"
        >
          <option value="">Todas as categorias</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <Input
          placeholder="Buscar por nome ou conteúdo…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Idioma</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Atualizado em</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-destructive">
                  Erro ao carregar templates.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && (data?.templates ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Nenhum template encontrado.
                </TableCell>
              </TableRow>
            )}
            {(data?.templates ?? []).map((t: Template) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>{t.language}</TableCell>
                <TableCell>{t.category}</TableCell>
                <TableCell>
                  <TemplateStatusBadge status={t.status} />
                </TableCell>
                <TableCell>{new Date(t.updatedAt).toLocaleString('pt-BR')}</TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/admin/templates/${t.id}`} aria-label={`Abrir ${t.name}`}>
                      <FileText className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </div>
  )
}

export default TemplatesPage
